import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ButtonStyleComponent } from 'projects/app-default/src/app/common/buttons/button-style/button-style.component';
import { NPitchPrint } from 'projects/app-default/src/app/module/pitch-print/pitch-print.namespace';
import { PitchPrintService } from 'projects/app-default/src/app/module/pitch-print/pitch-print.service';
import { Product } from '../../product.decorator';

export interface IProjectSavedEvent {
    productId: number;
    project: NPitchPrint.IProject;
}

@Component({
    standalone: true,
    selector: 'product-view-pitch-print-button',
    templateUrl: './product-view-pitch-print-button.html',
    imports: [ButtonStyleComponent, TranslationModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewPitchPrintButtonComponent extends UiComponent {
    @Input({ required: true })
    product!: Product<IProductDataV3>;

    @Input()
    project?: NPitchPrint.IProject;

    @Output()
    projectSaved = new EventEmitter<IProjectSavedEvent>();

    protected status = new Status();

    private pitchPrintService = inject(PitchPrintService);

    private requestService = inject(RequestService);

    private pitchPrintClient?: NPitchPrint.IClient;

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
    }

    override ngOnChanges(changes: any): void {
        if (changes['product']) {
            this.status.reset();
            this.pitchPrintClient = undefined;
            if (this.status.promise?.cancel) {
                this.status.promise.cancel();
            }
            const config = this.getPitchPrintClientConfig(this.product);
            if (config) {
                this.requestService.send(
                    this.status,
                    () => {
                        return this.pitchPrintService.createClient(config);
                    },
                    client => {
                        if (client) {
                            this.setPitchPrintClient(client);
                        }
                    }
                );
            }
            this.detectChanges();
        }
    }

    downloadPdf(): void {
        if (this.pitchPrintClient && this.project) {
            this.pitchPrintClient._triggerPdfDownload();
        }
    }

    initializeForNewProject(): void {
        this.status.reset();
        this.pitchPrintClient = undefined;
        if (this.status.promise?.cancel) {
            this.status.promise.cancel();
        }
        const config = this.getPitchPrintClientConfigForNewProject(this.product);
        if (config) {
            this.requestService.send(
                this.status,
                () => this.pitchPrintService.createClient(config),
                client => {
                    if (client) {
                        this.setPitchPrintClient(client);
                    }
                }
            );
        }
        this.detectChanges();
    }

    protected get isDisabled(): boolean {
        return !this.pitchPrintClient;
    }

    protected showApp(): void {
        if (this.pitchPrintClient) {
            this.pitchPrintClient.showApp();
        }
    }

    private setPitchPrintClient(client: NPitchPrint.IClient) {
        client.on('app-validated', e => {
            this.pitchPrintClient = client;
            this.detectChanges();
            client.on('project-saved', (e: NPitchPrint.IProjectSavedEvent) => {
                const project = e.data;
                if (project && this.product.id) {
                    this.pitchPrintService.addProjectToStorage(this.product.id, project);
                    this.projectSaved.emit({ productId: this.product.id, project: project });
                    this.detectChanges();
                }
            });
        });
    }

    private getPitchPrintClientConfig(product: Product<IProductDataV3>): Partial<NPitchPrint.IClientConfig> | undefined {
        const pitchPrintDesignId = this.product.pitchPrintDesignId;
        if (pitchPrintDesignId && product.data?.sku && product.data?.name) {
            const config: Partial<NPitchPrint.IClientConfig> = {
                designId: pitchPrintDesignId,
                product: {
                    id: product.id.toString(),
                    name: product.data.name
                }
            };
            const project = this.project;
            if (project) {
                config.projectId = project.projectId;
                config.mode = 'edit';
            }
            return config;
        }
        return undefined;
    }

    private getPitchPrintClientConfigForNewProject(product: Product<IProductDataV3>): Partial<NPitchPrint.IClientConfig> | undefined {
        const pitchPrintDesignId = this.product.pitchPrintDesignId;
        if (pitchPrintDesignId && product.data?.sku && product.data?.name) {
            return {
                designId: pitchPrintDesignId,
                product: {
                    id: product.id.toString(),
                    name: product.data.name
                },
                mode: 'new'
            };
        }
        return undefined;
    }
}
